import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Users, Eye, Clock, TrendingUp, TrendingDown, Globe, Monitor, 
  Smartphone, Tablet, MapPin, ArrowUpRight, ArrowDownRight,
  RefreshCw, Calendar, BarChart3, PieChart, Target, LogOut,
  MousePointer, Search, Share2, Mail, MessageSquare
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Simple chart components (no external library needed)
const SimpleBarChart = ({ data, valueKey, labelKey, maxItems = 10, color = '#7CD4FD' }) => {
  const items = data?.slice(0, maxItems) || [];
  const maxValue = Math.max(...items.map(d => d[valueKey] || 0), 1);
  
  return (
    <div className="space-y-2">
      {items.map((item, idx) => (
        <div key={idx} className="flex items-center gap-3">
          <div className="w-28 text-sm text-gray-400 truncate" title={item[labelKey]}>
            {item[labelKey] || 'Unknown'}
          </div>
          <div className="flex-1 h-6 bg-[#1F2937] rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{ 
                width: `${(item[valueKey] / maxValue) * 100}%`,
                backgroundColor: color
              }}
            />
          </div>
          <div className="w-16 text-sm text-white text-right font-medium">
            {item[valueKey]?.toLocaleString()}
          </div>
        </div>
      ))}
      {items.length === 0 && (
        <div className="text-center text-gray-500 py-4">Keine Daten verfügbar</div>
      )}
    </div>
  );
};

const SimplePieChart = ({ data, valueKey, labelKey, colors }) => {
  const items = data || [];
  const total = items.reduce((sum, d) => sum + (d[valueKey] || 0), 0);
  const defaultColors = ['#7CD4FD', '#22C55E', '#F59E0B', '#EF4444', '#A78BFA', '#EC4899', '#14B8A6', '#8B5CF6'];
  
  return (
    <div className="flex items-center gap-6">
      <div className="relative w-32 h-32">
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
          {items.reduce((acc, item, idx) => {
            const percentage = total > 0 ? (item[valueKey] / total) * 100 : 0;
            const offset = acc.offset;
            acc.elements.push(
              <circle
                key={idx}
                cx="50"
                cy="50"
                r="40"
                fill="none"
                stroke={(colors || defaultColors)[idx % defaultColors.length]}
                strokeWidth="20"
                strokeDasharray={`${percentage * 2.51} ${251 - percentage * 2.51}`}
                strokeDashoffset={-offset * 2.51}
                className="transition-all duration-500"
              />
            );
            acc.offset += percentage;
            return acc;
          }, { elements: [], offset: 0 }).elements}
        </svg>
      </div>
      <div className="flex-1 space-y-2">
        {items.map((item, idx) => (
          <div key={idx} className="flex items-center gap-2 text-sm">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: (colors || defaultColors)[idx % defaultColors.length] }}
            />
            <span className="text-gray-400 flex-1">{item[labelKey]}</span>
            <span className="text-white font-medium">
              {total > 0 ? Math.round((item[valueKey] / total) * 100) : 0}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const StatCard = ({ title, value, change, icon: Icon, suffix = '', loading }) => {
  const isPositive = change >= 0;
  
  return (
    <Card className="bg-[#0F141A] border-[#1F2937]">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-gray-400 mb-1">{title}</p>
            {loading ? (
              <div className="h-8 w-24 bg-[#1F2937] animate-pulse rounded" />
            ) : (
              <p className="text-3xl font-bold text-white">
                {typeof value === 'number' ? value.toLocaleString() : value}{suffix}
              </p>
            )}
            {change !== undefined && !loading && (
              <div className={`flex items-center gap-1 mt-2 text-sm ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
                {isPositive ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                <span>{Math.abs(change).toFixed(1)}%</span>
                <span className="text-gray-500 ml-1">vs. vorher</span>
              </div>
            )}
          </div>
          <div className="p-3 rounded-xl bg-[#1EA0E6]/10">
            <Icon className="w-6 h-6 text-[#1EA0E6]" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const TrafficSourceIcon = ({ source }) => {
  const iconMap = {
    google: <Search className="w-4 h-4" />,
    facebook: <MessageSquare className="w-4 h-4" />,
    instagram: <Share2 className="w-4 h-4" />,
    twitter: <MessageSquare className="w-4 h-4" />,
    linkedin: <Users className="w-4 h-4" />,
    email: <Mail className="w-4 h-4" />,
    direct: <MousePointer className="w-4 h-4" />,
    referral: <Share2 className="w-4 h-4" />
  };
  return iconMap[source?.toLowerCase()] || <Globe className="w-4 h-4" />;
};

export default function AdminAnalytics() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);
  const [overview, setOverview] = useState(null);
  const [sources, setSources] = useState(null);
  const [locations, setLocations] = useState(null);
  const [pages, setPages] = useState(null);
  const [exits, setExits] = useState(null);
  const [devices, setDevices] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      const [overviewRes, sourcesRes, locationsRes, pagesRes, exitsRes, devicesRes] = await Promise.all([
        axios.get(`${API}/api/analytics/admin/overview?days=${days}`, { headers }),
        axios.get(`${API}/api/analytics/admin/sources?days=${days}`, { headers }),
        axios.get(`${API}/api/analytics/admin/locations?days=${days}`, { headers }),
        axios.get(`${API}/api/analytics/admin/pages?days=${days}`, { headers }),
        axios.get(`${API}/api/analytics/admin/exits?days=${days}`, { headers }),
        axios.get(`${API}/api/analytics/admin/devices?days=${days}`, { headers })
      ]);
      
      setOverview(overviewRes.data);
      setSources(sourcesRes.data);
      setLocations(locationsRes.data);
      setPages(pagesRes.data);
      setExits(exitsRes.data);
      setDevices(devicesRes.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Unbekannter Fehler';
      setError(`Fehler beim Laden der Statistiken: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  return (
    <div className="p-6 space-y-6" data-testid="admin-analytics">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-[#1EA0E6]" />
            Besucherstatistik
          </h1>
          <p className="text-gray-400 mt-1">
            Analysieren Sie Traffic-Quellen, Besucherverhalten und Conversion-Pfade
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Time Range Selector */}
          <div className="flex items-center gap-2 bg-[#0F141A] rounded-lg p-1 border border-[#1F2937]">
            {[7, 14, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  days === d 
                    ? 'bg-[#1EA0E6] text-white' 
                    : 'text-gray-400 hover:text-white hover:bg-[#1F2937]'
                }`}
              >
                {d} Tage
              </button>
            ))}
          </div>
          
          <Button
            onClick={fetchData}
            variant="outline"
            className="border-[#1F2937] text-gray-400 hover:text-white"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
          {error}
        </div>
      )}

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          title="Seitenaufrufe" 
          value={overview?.summary?.pageviews || 0}
          change={overview?.summary?.pageviews_change}
          icon={Eye}
          loading={loading}
        />
        <StatCard 
          title="Unique Besucher" 
          value={overview?.summary?.unique_visitors || 0}
          change={overview?.summary?.visitors_change}
          icon={Users}
          loading={loading}
        />
        <StatCard 
          title="Absprungrate" 
          value={overview?.summary?.bounce_rate || 0}
          suffix="%"
          icon={LogOut}
          loading={loading}
        />
        <StatCard 
          title="Ø Sitzungsdauer" 
          value={formatDuration(overview?.summary?.avg_session_duration)}
          icon={Clock}
          loading={loading}
        />
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-[#1F2937] pb-2">
        {[
          { id: 'overview', label: 'Übersicht', icon: BarChart3 },
          { id: 'sources', label: 'Traffic-Quellen', icon: Share2 },
          { id: 'locations', label: 'Standorte', icon: MapPin },
          { id: 'pages', label: 'Seiten', icon: Eye },
          { id: 'exits', label: 'Absprünge', icon: LogOut },
          { id: 'devices', label: 'Geräte', icon: Monitor }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-[#1EA0E6] text-white'
                : 'text-gray-400 hover:text-white hover:bg-[#1F2937]'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Daily Traffic Chart */}
            <Card className="bg-[#0F141A] border-[#1F2937] lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-[#1EA0E6]" />
                  Täglicher Traffic
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="h-64 bg-[#1F2937] animate-pulse rounded" />
                ) : (
                  <div className="h-64 flex items-end gap-1">
                    {(overview?.daily || []).map((day, idx) => {
                      const maxViews = Math.max(...(overview?.daily || []).map(d => d.pageviews || 0), 1);
                      return (
                        <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                          <div 
                            className="w-full bg-gradient-to-t from-[#1EA0E6] to-[#7CD4FD] rounded-t transition-all duration-500 hover:opacity-80"
                            style={{ height: `${(day.pageviews / maxViews) * 200}px`, minHeight: '4px' }}
                            title={`${day._id}: ${day.pageviews} Aufrufe, ${day.visitors} Besucher`}
                          />
                          <span className="text-[10px] text-gray-500 rotate-45 origin-left">
                            {day._id?.slice(5)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Top Traffic Sources */}
            <Card className="bg-[#0F141A] border-[#1F2937]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-[#22C55E]" />
                  Top Traffic-Quellen
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(sources?.sources || []).slice(0, 5).map((source, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-[#1F2937]/50 hover:bg-[#1F2937] transition-colors">
                      <div className="p-2 rounded-lg bg-[#1EA0E6]/10">
                        <TrafficSourceIcon source={source.source} />
                      </div>
                      <div className="flex-1">
                        <div className="text-white font-medium capitalize">{source.source || 'Direkt'}</div>
                        <div className="text-sm text-gray-400">{source.visitors} Besucher</div>
                      </div>
                      <div className="text-right">
                        <div className="text-white font-bold">{source.visits}</div>
                        <div className="text-xs text-gray-500">Aufrufe</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Top Countries */}
            <Card className="bg-[#0F141A] border-[#1F2937]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Globe className="w-5 h-5 text-[#F59E0B]" />
                  Top Länder
                </CardTitle>
              </CardHeader>
              <CardContent>
                <SimpleBarChart 
                  data={locations?.countries || []}
                  valueKey="visits"
                  labelKey="country"
                  maxItems={8}
                  color="#F59E0B"
                />
              </CardContent>
            </Card>
          </>
        )}

        {/* Sources Tab */}
        {activeTab === 'sources' && (
          <>
            <Card className="bg-[#0F141A] border-[#1F2937]">
              <CardHeader>
                <CardTitle className="text-white">Traffic-Quellen Verteilung</CardTitle>
              </CardHeader>
              <CardContent>
                <SimplePieChart 
                  data={sources?.sources || []}
                  valueKey="visits"
                  labelKey="source"
                />
              </CardContent>
            </Card>

            <Card className="bg-[#0F141A] border-[#1F2937]">
              <CardHeader>
                <CardTitle className="text-white">Alle Traffic-Quellen</CardTitle>
              </CardHeader>
              <CardContent>
                <SimpleBarChart 
                  data={sources?.sources || []}
                  valueKey="visits"
                  labelKey="source"
                  maxItems={10}
                  color="#22C55E"
                />
              </CardContent>
            </Card>

            <Card className="bg-[#0F141A] border-[#1F2937] lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-white">Kampagnen (UTM)</CardTitle>
              </CardHeader>
              <CardContent>
                {(sources?.campaigns || []).length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-gray-400 text-sm border-b border-[#1F2937]">
                          <th className="pb-3 pr-4">Quelle</th>
                          <th className="pb-3 pr-4">Medium</th>
                          <th className="pb-3 pr-4">Kampagne</th>
                          <th className="pb-3 pr-4 text-right">Besucher</th>
                          <th className="pb-3 text-right">Aufrufe</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sources.campaigns.map((campaign, idx) => (
                          <tr key={idx} className="border-b border-[#1F2937]/50 hover:bg-[#1F2937]/30">
                            <td className="py-3 pr-4 text-white">{campaign.source || '-'}</td>
                            <td className="py-3 pr-4 text-gray-400">{campaign.medium || '-'}</td>
                            <td className="py-3 pr-4 text-[#1EA0E6]">{campaign.campaign}</td>
                            <td className="py-3 pr-4 text-right text-white">{campaign.visitors}</td>
                            <td className="py-3 text-right text-white font-medium">{campaign.visits}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Keine UTM-Kampagnen gefunden</p>
                    <p className="text-sm mt-2">
                      Verwenden Sie UTM-Parameter in Ihren Marketing-Links:<br />
                      <code className="text-[#1EA0E6]">?utm_source=facebook&utm_medium=social&utm_campaign=launch</code>
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Locations Tab */}
        {activeTab === 'locations' && (
          <>
            <Card className="bg-[#0F141A] border-[#1F2937]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#F59E0B]" />
                  Besucher nach Land
                </CardTitle>
              </CardHeader>
              <CardContent>
                <SimpleBarChart 
                  data={locations?.countries || []}
                  valueKey="visits"
                  labelKey="country"
                  maxItems={15}
                  color="#F59E0B"
                />
              </CardContent>
            </Card>

            <Card className="bg-[#0F141A] border-[#1F2937]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Globe className="w-5 h-5 text-[#A78BFA]" />
                  Sprachen
                </CardTitle>
              </CardHeader>
              <CardContent>
                <SimpleBarChart 
                  data={locations?.languages || []}
                  valueKey="visits"
                  labelKey="language"
                  maxItems={10}
                  color="#A78BFA"
                />
              </CardContent>
            </Card>
          </>
        )}

        {/* Pages Tab */}
        {activeTab === 'pages' && (
          <>
            <Card className="bg-[#0F141A] border-[#1F2937]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Eye className="w-5 h-5 text-[#1EA0E6]" />
                  Beliebteste Seiten
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {(pages?.top_pages || []).map((page, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-[#1F2937]/50 hover:bg-[#1F2937] transition-colors">
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-medium truncate" title={page.page}>
                          {page.title || page.page?.split('/').pop() || '/'}
                        </div>
                        <div className="text-xs text-gray-500 truncate">{page.page}</div>
                      </div>
                      <div className="text-right ml-4">
                        <div className="text-white font-bold">{page.views?.toLocaleString()}</div>
                        <div className="text-xs text-gray-500">{page.visitors} Besucher</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#0F141A] border-[#1F2937]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <ArrowUpRight className="w-5 h-5 text-[#22C55E]" />
                  Einstiegsseiten
                </CardTitle>
              </CardHeader>
              <CardContent>
                <SimpleBarChart 
                  data={pages?.entry_pages || []}
                  valueKey="entries"
                  labelKey="page"
                  maxItems={10}
                  color="#22C55E"
                />
              </CardContent>
            </Card>
          </>
        )}

        {/* Exits Tab */}
        {activeTab === 'exits' && (
          <>
            <Card className="bg-[#0F141A] border-[#1F2937]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <LogOut className="w-5 h-5 text-[#EF4444]" />
                  Ausstiegsseiten
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {(exits?.exit_pages || []).map((page, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-[#1F2937]/50 hover:bg-[#1F2937] transition-colors">
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-medium truncate" title={page.page}>
                          {page.page?.split('/').pop() || '/'}
                        </div>
                        <div className="text-xs text-gray-500">Ø {formatDuration(page.avg_duration)} auf Seite</div>
                      </div>
                      <div className="text-right ml-4">
                        <div className="text-[#EF4444] font-bold">{page.exits}</div>
                        <div className="text-xs text-gray-500">Ausstiege</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#0F141A] border-[#1F2937]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <TrendingDown className="w-5 h-5 text-[#F59E0B]" />
                  Absprungrate nach Seite
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {(exits?.bounce_by_page || []).map((page, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-[#1F2937]/50">
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-medium truncate">{page.page?.split('/').pop() || '/'}</div>
                        <div className="text-xs text-gray-500">{page.total} Besuche, {page.bounces} Absprünge</div>
                      </div>
                      <div className={`font-bold ${page.bounce_rate > 70 ? 'text-[#EF4444]' : page.bounce_rate > 50 ? 'text-[#F59E0B]' : 'text-[#22C55E]'}`}>
                        {page.bounce_rate?.toFixed(0)}%
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#0F141A] border-[#1F2937] lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-white">Absprung-Gründe</CardTitle>
              </CardHeader>
              <CardContent>
                <SimplePieChart 
                  data={exits?.exit_reasons || []}
                  valueKey="count"
                  labelKey="reason"
                  colors={['#EF4444', '#F59E0B', '#22C55E', '#1EA0E6', '#A78BFA']}
                />
              </CardContent>
            </Card>
          </>
        )}

        {/* Devices Tab */}
        {activeTab === 'devices' && (
          <>
            <Card className="bg-[#0F141A] border-[#1F2937]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Monitor className="w-5 h-5 text-[#1EA0E6]" />
                  Gerätetypen
                </CardTitle>
              </CardHeader>
              <CardContent>
                <SimplePieChart 
                  data={(devices?.devices || []).map(d => ({
                    ...d,
                    device: d.device === 'desktop' ? 'Desktop' : d.device === 'mobile' ? 'Mobile' : d.device === 'tablet' ? 'Tablet' : d.device
                  }))}
                  valueKey="visits"
                  labelKey="device"
                />
              </CardContent>
            </Card>

            <Card className="bg-[#0F141A] border-[#1F2937]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Globe className="w-5 h-5 text-[#22C55E]" />
                  Browser
                </CardTitle>
              </CardHeader>
              <CardContent>
                <SimpleBarChart 
                  data={devices?.browsers || []}
                  valueKey="visits"
                  labelKey="browser"
                  maxItems={8}
                  color="#22C55E"
                />
              </CardContent>
            </Card>

            <Card className="bg-[#0F141A] border-[#1F2937] lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-[#A78BFA]" />
                  Betriebssysteme
                </CardTitle>
              </CardHeader>
              <CardContent>
                <SimpleBarChart 
                  data={devices?.operating_systems || []}
                  valueKey="visits"
                  labelKey="os"
                  maxItems={10}
                  color="#A78BFA"
                />
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Marketing Tips */}
      <Card className="bg-gradient-to-br from-[#1EA0E6]/10 to-[#22C55E]/10 border-[#1EA0E6]/30">
        <CardContent className="p-6">
          <h3 className="text-white font-bold mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-[#1EA0E6]" />
            Marketing-Tipps
          </h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="p-4 rounded-lg bg-black/20">
              <h4 className="text-[#1EA0E6] font-semibold mb-2">UTM-Parameter nutzen</h4>
              <p className="text-gray-400">
                Fügen Sie UTM-Parameter zu Ihren Marketing-Links hinzu, um Kampagnen präzise zu tracken.
              </p>
            </div>
            <div className="p-4 rounded-lg bg-black/20">
              <h4 className="text-[#22C55E] font-semibold mb-2">Absprungrate reduzieren</h4>
              <p className="text-gray-400">
                Seiten mit hoher Absprungrate optimieren: schnellere Ladezeit, besserer Content, klare CTAs.
              </p>
            </div>
            <div className="p-4 rounded-lg bg-black/20">
              <h4 className="text-[#F59E0B] font-semibold mb-2">Top-Quellen verstärken</h4>
              <p className="text-gray-400">
                Investieren Sie mehr in Traffic-Quellen mit hoher Conversion-Rate und niedrigem Bounce.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
